import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useUpload } from '../hooks/useUpload.js';
import { useJobPolling } from '../hooks/useJobPolling.js';
import { PRESETS } from '../lib/templates.js';
import { generateConfig, generateExtendConfig } from '../lib/config-generator.js';
import { getProject, updateProject } from '../api/projects.js';
import { listAssets, deleteAsset } from '../api/uploads.js';
import { createJob, getJob, listJobs, downloadJob } from '../api/jobs.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useToast } from '../components/Toast.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';

const STATUS_STYLES = {
  draft: 'bg-gray-700 text-gray-300',
  ready: 'bg-blue-900/50 text-blue-300',
  rendering: 'bg-yellow-900/50 text-yellow-300',
  completed: 'bg-green-900/50 text-green-300',
  failed: 'bg-red-900/50 text-red-300',
  archived: 'bg-gray-800 text-gray-500',
};

const FILE_STATUS_STYLES = {
  pending: 'bg-gray-700 text-gray-300',
  requesting: 'bg-yellow-900/50 text-yellow-300',
  uploading: 'bg-blue-900/50 text-blue-300',
  confirming: 'bg-blue-900/50 text-blue-300',
  confirmed: 'bg-green-900/50 text-green-300',
  error: 'bg-red-900/50 text-red-300',
};

const JOB_STATUS_STYLES = {
  queued: 'bg-gray-700 text-gray-300',
  processing: 'bg-yellow-900/50 text-yellow-300',
  completed: 'bg-green-900/50 text-green-300',
  failed: 'bg-red-900/50 text-red-300',
};

const EXTEND_HOURS = [1, 2, 3, 4, 8];

const PRESET_ICONS = {
  ambient_loop: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12a3 3 0 106 0 3 3 0 00-6 0z" />
    </svg>
  ),
  luxury_showcase: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
    </svg>
  ),
  deep_nostalgia: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatElapsed(startIso) {
  if (!startIso) return '0:00';
  const elapsed = Math.max(0, Math.floor((Date.now() - new Date(startIso).getTime()) / 1000));
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isImageType(contentType) {
  return contentType?.startsWith('image/');
}

function isAudioType(contentType) {
  return contentType?.startsWith('audio/');
}

function FileTypeIcon({ contentType }) {
  if (isImageType(contentType)) {
    return (
      <svg className="w-5 h-5 text-indigo-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  }
  if (isAudioType(contentType)) {
    return (
      <svg className="w-5 h-5 text-purple-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

// Elapsed time component that ticks every second
function ElapsedTimer({ startIso }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);
  return <span>{formatElapsed(startIso)}</span>;
}

export default function ProjectPage() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const { subscription } = useAuth();
  const { showToast } = useToast();

  // Project state
  const [project, setProject] = useState(null);
  const [assets, setAssets] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Project name editing
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const nameInputRef = useRef(null);

  // Upload
  const { files, addFiles, startUpload, removeFile, retryFile, isUploading } = useUpload();
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const wasUploadingRef = useRef(false);

  // Config
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [imageDuration, setImageDuration] = useState(10);
  const [transitionDuration, setTransitionDuration] = useState(1.0);
  const [sequencing, setSequencing] = useState('shuffle');

  // Generate
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  // Active job polling
  const [activeJobId, setActiveJobId] = useState(null);
  const { job: activeJob, isPolling } = useJobPolling(activeJobId, !!activeJobId);

  // Extension
  const [extendHours, setExtendHours] = useState(2);
  const [isExtending, setIsExtending] = useState(false);

  // Derived: combine uploaded files with server-side assets
  const confirmedAssets = [
    ...assets,
    ...files
      .filter((f) => f.status === 'confirmed' && !assets.some((a) => a.id === f.assetId))
      .map((f) => ({
        id: f.assetId,
        filename: f.name,
        content_type: f.type,
        status: 'ready',
        size_bytes: f.size,
      })),
  ];

  const imageAssets = confirmedAssets.filter((a) => isImageType(a.content_type));
  const audioAssets = confirmedAssets.filter((a) => isAudioType(a.content_type));

  const canGenerate = imageAssets.length >= 1 && selectedPreset !== null && !isGenerating && !isUploading;

  // Tier-based resolution
  const tierResolution = subscription?.tier === 'pro' ? '3840x2160' : '1920x1080';

  // Load project data
  useEffect(() => {
    let cancelled = false;

    async function loadProject() {
      setIsLoading(true);
      setError(null);
      try {
        const [proj, assetsData, jobsData] = await Promise.all([
          getProject(projectId),
          listAssets(projectId),
          listJobs(projectId),
        ]);
        if (cancelled) return;
        setProject(proj);
        setAssets(assetsData.items || []);

        const jobItems = jobsData.items || [];
        setJobs(jobItems);

        // If there's an active job, check if it's stale before polling
        const active = jobItems.find(
          (j) => j.status === 'queued' || j.status === 'processing'
        );
        if (active) {
          const jobAge = Date.now() - new Date(active.created_at).getTime();
          const MAX_JOB_AGE_MS = 35 * 60 * 1000; // 35 minutes
          if (jobAge < MAX_JOB_AGE_MS) {
            setActiveJobId(active.id);
          }
          // else: job is stale (likely timed out), don't poll
        }

        // Fetch full details for latest completed job (includes download_url)
        const latestCompleted = jobItems.find((j) => j.status === 'completed');
        const isActivePolling = active && (Date.now() - new Date(active.created_at).getTime()) < 35 * 60 * 1000;
        if (latestCompleted && !isActivePolling) {
          try {
            const fullJob = await getJob(latestCompleted.id);
            setJobs((prev) => prev.map((j) => j.id === fullJob.id ? fullJob : j));
          } catch {
            // silent — review section just won't show
          }
        }
      } catch (err) {
        if (cancelled) return;
        if (err.status === 404 || err.status === 422) {
          setError('Project not found');
        } else {
          setError(err.message || 'Failed to load project');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadProject();
    return () => { cancelled = true; };
  }, [projectId]);

  // When active job completes or fails, refresh job list and show toast
  useEffect(() => {
    if (activeJob && activeJob.status === 'completed') {
      showToast('Render completed successfully!', 'success');
      listJobs(projectId)
        .then((data) => setJobs(data.items || []))
        .catch(() => {});
    } else if (activeJob && activeJob.status === 'failed') {
      showToast(activeJob.error_message || 'Render job failed', 'error');
      listJobs(projectId)
        .then((data) => setJobs(data.items || []))
        .catch(() => {});
    }
  }, [activeJob?.status, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toast when upload batch completes
  useEffect(() => {
    if (wasUploadingRef.current && !isUploading && files.length > 0) {
      const confirmedCount = files.filter((f) => f.status === 'confirmed').length;
      const errorCount = files.filter((f) => f.status === 'error').length;
      if (confirmedCount > 0) {
        showToast(`${confirmedCount} file${confirmedCount !== 1 ? 's' : ''} uploaded`, 'success');
      }
      if (errorCount > 0) {
        showToast(`${errorCount} upload${errorCount !== 1 ? 's' : ''} failed`, 'error');
      }
    }
    wasUploadingRef.current = isUploading;
  }, [isUploading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus name input when editing
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  // -- Handlers --

  function handleStartEditName() {
    setEditName(project?.name || '');
    setIsEditingName(true);
  }

  async function handleSaveName() {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === project?.name) {
      setIsEditingName(false);
      return;
    }
    setIsSavingName(true);
    try {
      const updated = await updateProject(projectId, { name: trimmed });
      setProject(updated);
      showToast('Project renamed', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to rename project', 'error');
    } finally {
      setIsSavingName(false);
      setIsEditingName(false);
    }
  }

  function handleNameKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveName();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  }

  // Drag and drop
  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
      startUpload(projectId);
    }
  }

  function handleFileSelect(e) {
    if (e.target.files.length > 0) {
      addFiles(e.target.files);
      startUpload(projectId);
    }
    // Reset input so same file can be re-selected
    e.target.value = '';
  }

  async function handleRemoveAsset(assetId) {
    try {
      await deleteAsset(projectId, assetId);
      setAssets((prev) => prev.filter((a) => a.id !== assetId));
      showToast('Asset removed', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to remove asset', 'error');
    }
  }

  function handleSelectPreset(key) {
    setSelectedPreset(key);
    const preset = PRESETS[key];
    setImageDuration(preset.image_duration);
    setTransitionDuration(preset.transition_duration);
    setSequencing(preset.sequencing);
  }

  async function handleGenerate() {
    if (!canGenerate) return;
    setIsGenerating(true);
    setGenerateError(null);

    try {
      const preset = PRESETS[selectedPreset];
      const overrides = { image_duration: imageDuration, transition_duration: transitionDuration, sequencing };
      const config = generateConfig(preset, imageAssets, audioAssets, overrides, tierResolution);

      const updatedProject = await updateProject(projectId, { configuration: config });
      setProject(updatedProject);
      const job = await createJob(projectId);

      setActiveJobId(job.id);
      setJobs((prev) => [job, ...prev]);
      showToast('Render job started', 'success');
    } catch (err) {
      if (err.status === 402) {
        setGenerateError({
          type: 'no_subscription',
          message: 'You need an active subscription to render videos.',
        });
      } else if (err.status === 429) {
        const detail = err.data?.detail || '';
        setGenerateError({
          type: 'credit_limit',
          message: typeof detail === 'string' ? detail : 'You have reached your credit limit for this billing period.',
          creditsRemaining: err.data?.credits_remaining,
          periodEnd: err.data?.period_end,
        });
      } else if (err.status === 403) {
        setGenerateError({
          type: 'tier_limit',
          message: err.data?.detail || 'This action exceeds your current tier limits.',
        });
      } else {
        showToast(err.message || 'Failed to start render', 'error');
      }
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleExtend() {
    setIsExtending(true);
    setGenerateError(null);

    try {
      // Get the GCS output path from the latest completed job
      const completedJob = (activeJob?.status === 'completed' ? activeJob : null)
        || jobs.find((j) => j.status === 'completed');
      if (!completedJob || !completedJob.output_url) {
        showToast('No completed master to extend. Generate a master first.', 'error');
        setIsExtending(false);
        return;
      }

      // Send extend-only config — no clips/assemblies, so the worker just stream-loops
      const extendConfig = generateExtendConfig(extendHours, completedJob.output_url, audioAssets);
      const updatedProject = await updateProject(projectId, { configuration: extendConfig });
      setProject(updatedProject);
      const job = await createJob(projectId);

      setActiveJobId(job.id);
      setJobs((prev) => [job, ...prev]);
      showToast('Extension job started', 'success');
    } catch (err) {
      if (err.status === 402) {
        setGenerateError({
          type: 'no_subscription',
          message: 'You need an active subscription to extend videos.',
        });
      } else if (err.status === 429) {
        setGenerateError({
          type: 'credit_limit',
          message: err.data?.detail || 'You have reached your credit limit for this billing period.',
        });
      } else if (err.status === 403) {
        setGenerateError({
          type: 'tier_limit',
          message: err.data?.detail || 'This action exceeds your current tier limits.',
        });
      } else {
        showToast(err.message || 'Failed to start extension', 'error');
      }
    } finally {
      setIsExtending(false);
    }
  }

  function handleRetryJob() {
    setActiveJobId(null);
    setGenerateError(null);
  }

  const refreshJobs = useCallback(async () => {
    try {
      const data = await listJobs(projectId);
      setJobs(data.items || []);
    } catch {
      // silent
    }
  }, [projectId]);

  // -- Render --

  if (isLoading) {
    return <LoadingSpinner message="Loading project..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
        <p className="text-red-400">{error}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  // Determine the most recent completed job for review
  const latestCompletedJob = activeJob?.status === 'completed'
    ? activeJob
    : jobs.find((j) => j.status === 'completed');

  // Max extend hours based on tier
  const maxHours = subscription?.tier === 'pro' ? 8 : 4;
  const availableExtendHours = EXTEND_HOURS.filter((h) => h <= maxHours);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* -- Project Header -- */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white transition-colors flex-shrink-0 p-1"
            title="Back to Dashboard"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>

          {isEditingName ? (
            <input
              ref={nameInputRef}
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleSaveName}
              onKeyDown={handleNameKeyDown}
              disabled={isSavingName}
              maxLength={255}
              className="text-2xl font-bold bg-gray-800 border border-gray-700 rounded-lg px-3 py-1 text-white focus:outline-none focus:border-indigo-500 disabled:opacity-50 min-w-0 w-full"
            />
          ) : (
            <h1
              onClick={handleStartEditName}
              className="text-2xl font-bold text-white cursor-pointer hover:text-indigo-300 transition-colors truncate"
              title="Click to rename"
            >
              {project?.name || 'Untitled Project'}
            </h1>
          )}

          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${STATUS_STYLES[project?.status] || STATUS_STYLES.draft}`}>
            {project?.status || 'draft'}
          </span>
        </div>
      </div>

      {/* -- Active Job: Render Status -- */}
      {activeJobId && activeJob && (activeJob.status === 'queued' || activeJob.status === 'processing') && (
        <RenderStatusSection job={activeJob} />
      )}

      {/* -- Active Job: Failed -- */}
      {activeJobId && activeJob && activeJob.status === 'failed' && (
        <div className="bg-gray-900 border border-red-800 rounded-lg p-6 mb-8">
          <div className="flex items-center gap-3 mb-3">
            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h3 className="text-lg font-semibold text-red-300">Render Failed</h3>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            {activeJob.error_message || 'An unexpected error occurred during rendering.'}
          </p>
          <button
            onClick={handleRetryJob}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm"
          >
            Try Again
          </button>
        </div>
      )}

      {/* -- Review Section (Completed Job) -- */}
      {latestCompletedJob && (!activeJobId || activeJob?.status === 'completed' || activeJob?.status === 'failed') && (
        <ReviewSection
          job={latestCompletedJob}
          extendHours={extendHours}
          setExtendHours={setExtendHours}
          availableExtendHours={availableExtendHours}
          isExtending={isExtending}
          onExtend={handleExtend}
          onDownloadReady={(url) => {
            // Update job state so subsequent clicks use cached R2 URL
            setJobs((prev) => prev.map((j) => j.id === latestCompletedJob.id ? { ...j, download_url: url } : j));
          }}
        />
      )}

      {/* -- Error Prompts (402, 429, 403) -- */}
      {generateError && (
        <div className="bg-gray-900 border border-yellow-800 rounded-lg p-6 mb-8">
          <div className="flex items-center gap-3 mb-3">
            <svg className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h3 className="text-lg font-semibold text-yellow-300">
              {generateError.type === 'no_subscription' && 'Subscription Required'}
              {generateError.type === 'credit_limit' && 'Credit Limit Reached'}
              {generateError.type === 'tier_limit' && 'Tier Limit Exceeded'}
            </h3>
          </div>
          <p className="text-gray-400 text-sm mb-2">{generateError.message}</p>
          {generateError.periodEnd && (
            <p className="text-gray-500 text-xs mb-4">
              Credits reset on {formatDate(generateError.periodEnd)}
            </p>
          )}
          <div className="flex gap-3">
            <Link
              to="/pricing"
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm"
            >
              {generateError.type === 'no_subscription' ? 'View Plans' : 'Upgrade Plan'}
            </Link>
            <button
              onClick={() => setGenerateError(null)}
              className="px-4 py-2 text-gray-400 hover:text-white text-sm"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* -- Upload Zone -- */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">Assets</h2>

        {/* Drop zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragOver
              ? 'border-indigo-500 bg-indigo-500/10'
              : 'border-gray-700 hover:border-gray-600 bg-gray-900'
          }`}
        >
          <svg className="w-10 h-10 mx-auto text-gray-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="text-gray-300 mb-1">
            {isDragOver ? 'Drop files here' : 'Drag and drop files or click to browse'}
          </p>
          <p className="text-gray-500 text-xs">
            Images: JPEG, PNG, WebP, GIF &middot; Audio: MP3, WAV, FLAC, AAC, OGG
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif,audio/mpeg,audio/wav,audio/flac,audio/aac,audio/ogg"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Uploading files */}
        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            {files.map((f) => (
              <div key={f.id} className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3">
                <div className="flex items-center gap-3">
                  <FileTypeIcon contentType={f.type} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white truncate">{f.name}</span>
                      <span className="text-xs text-gray-500">{formatFileSize(f.size)}</span>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${FILE_STATUS_STYLES[f.status] || ''}`}>
                        {f.status}
                      </span>
                    </div>
                    {(f.status === 'uploading' || f.status === 'requesting' || f.status === 'confirming') && (
                      <div className="mt-1.5 w-full bg-gray-800 rounded-full h-1.5">
                        <div
                          className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${f.progress}%` }}
                        />
                      </div>
                    )}
                    {f.status === 'error' && f.error && (
                      <p className="text-red-400 text-xs mt-1">{f.error}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {f.status === 'error' && (
                      <button
                        onClick={() => { retryFile(f.id); startUpload(projectId); }}
                        className="text-indigo-400 hover:text-indigo-300 text-xs px-2 py-1"
                        title="Retry"
                      >
                        Retry
                      </button>
                    )}
                    <button
                      onClick={() => removeFile(f.id)}
                      className="text-gray-500 hover:text-red-400 transition-colors"
                      title="Remove"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Confirmed assets from server */}
        {confirmedAssets.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-400 mb-2">
              Uploaded ({imageAssets.length} image{imageAssets.length !== 1 ? 's' : ''}, {audioAssets.length} audio)
            </h3>
            <div className="space-y-2">
              {confirmedAssets.map((asset) => (
                <div key={asset.id} className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 flex items-center gap-3">
                  <FileTypeIcon contentType={asset.content_type} />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-white truncate block">{asset.filename}</span>
                    <span className="text-xs text-gray-500">{formatFileSize(asset.size_bytes)}</span>
                  </div>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-900/50 text-green-300">
                    ready
                  </span>
                  <button
                    onClick={() => handleRemoveAsset(asset.id)}
                    className="text-gray-500 hover:text-red-400 transition-colors flex-shrink-0"
                    title="Remove asset"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* -- Template Selector -- */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">Template</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {Object.entries(PRESETS).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => handleSelectPreset(key)}
              className={`text-left p-4 rounded-lg border transition-colors ${
                selectedPreset === key
                  ? 'border-indigo-500 bg-indigo-500/10'
                  : 'border-gray-800 bg-gray-900 hover:border-gray-700'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={selectedPreset === key ? 'text-indigo-400' : 'text-gray-500'}>
                  {PRESET_ICONS[key]}
                </span>
                <span className="font-medium text-white">{preset.name}</span>
              </div>
              <p className="text-gray-400 text-xs leading-relaxed">{preset.description}</p>
              <div className="mt-3 flex gap-2 text-xs text-gray-500">
                <span>{preset.image_duration}s</span>
                <span>&middot;</span>
                <span>{preset.transition_duration}s fade</span>
                <span>&middot;</span>
                <span className="capitalize">{preset.sequencing}</span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* -- Config Panel -- */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">Configuration</h2>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Image Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Image Duration: <span className="text-white">{imageDuration}s</span>
              </label>
              <input
                type="range"
                min="3"
                max="20"
                step="0.5"
                value={imageDuration}
                onChange={(e) => setImageDuration(parseFloat(e.target.value))}
                className="w-full accent-indigo-500"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>3s</span>
                <span>20s</span>
              </div>
            </div>

            {/* Transition Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Transition Duration: <span className="text-white">{transitionDuration.toFixed(1)}s</span>
              </label>
              <input
                type="range"
                min="0.1"
                max="3.0"
                step="0.1"
                value={transitionDuration}
                onChange={(e) => setTransitionDuration(parseFloat(e.target.value))}
                className="w-full accent-indigo-500"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0.1s</span>
                <span>3.0s</span>
              </div>
            </div>
          </div>

          {/* Sequencing Toggle */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Sequencing</label>
            <div className="flex gap-2">
              <button
                onClick={() => setSequencing('linear')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  sequencing === 'linear'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                Linear
              </button>
              <button
                onClick={() => setSequencing('shuffle')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  sequencing === 'shuffle'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                Shuffle
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* -- Generate Button -- */}
      <section className="mb-8">
        <button
          onClick={handleGenerate}
          disabled={!canGenerate}
          className="w-full sm:w-auto px-8 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingSpinner size="sm" />
              Generating...
            </span>
          ) : (
            'Generate Master'
          )}
        </button>
        {!canGenerate && !isGenerating && (
          <p className="text-gray-500 text-xs mt-2">
            {imageAssets.length === 0 && 'Upload at least one image to get started.'}
            {imageAssets.length > 0 && !selectedPreset && 'Select a template above.'}
          </p>
        )}
      </section>

      {/* -- Job History -- */}
      {jobs.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-white">Job History</h2>
              {isPolling && (
                <span className="flex items-center gap-1.5 text-xs text-indigo-400">
                  <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-pulse" />
                  Updating
                </span>
              )}
            </div>
            <button
              onClick={refreshJobs}
              className="text-gray-500 hover:text-white transition-colors p-1"
              title="Refresh job list"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          <div className="space-y-2">
            {jobs.map((job) => (
              <div key={job.id} className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${JOB_STATUS_STYLES[job.status] || 'bg-gray-700 text-gray-300'}`}>
                    {job.status}
                  </span>
                  <span className="text-xs text-gray-500 capitalize">{job.job_type || 'render'}</span>
                  <span className="text-xs text-gray-500">{formatDate(job.created_at || job.started_at)}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {job.status === 'completed' && job.download_url && (
                    <a
                      href={job.download_url}
                      download
                      className="text-indigo-400 hover:text-indigo-300 text-xs font-medium"
                    >
                      Download
                    </a>
                  )}
                  {job.status === 'failed' && job.error_message && (
                    <span className="text-red-400 text-xs truncate max-w-[200px]" title={job.error_message}>
                      {job.error_message}
                    </span>
                  )}
                  {job.output_metadata?.file_size && (
                    <span className="text-xs text-gray-500">{formatFileSize(job.output_metadata.file_size)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// -- Sub-components --

function RenderStatusSection({ job }) {
  const startTime = job.started_at || job.created_at;
  const statusLabel = job.status === 'processing' ? 'Processing' : 'Queued';

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-white">{statusLabel}</h3>
            <p className="text-gray-400 text-sm">Your video is being rendered...</p>
          </div>
        </div>
        <div className="text-right sm:text-right text-left pl-11 sm:pl-0">
          <span className="text-sm text-gray-400">Elapsed</span>
          <p className="text-white font-mono">
            <ElapsedTimer startIso={startTime} />
          </p>
        </div>
      </div>

      {/* Indeterminate progress bar */}
      <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
        <div className="bg-indigo-500 h-2 rounded-full animate-indeterminate" />
      </div>

      <style>{`
        @keyframes indeterminate {
          0% { width: 0%; margin-left: 0; }
          50% { width: 40%; margin-left: 30%; }
          100% { width: 0%; margin-left: 100%; }
        }
        .animate-indeterminate {
          animation: indeterminate 1.8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

function DownloadButton({ job, onDownloadReady }) {
  const [state, setState] = useState('idle');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);

  async function handleDownload() {
    // Cache hit — immediate download
    if (job.download_url) {
      window.open(job.download_url, '_blank');
      return;
    }
    // Cache miss — trigger extension
    setState('preparing');
    setElapsed(0);
    setError(null);
    const timer = setInterval(() => setElapsed(e => e + 1), 1000);
    try {
      const result = await downloadJob(job.id);
      clearInterval(timer);
      setState('idle');
      if (onDownloadReady) onDownloadReady(result.download_url);
      window.open(result.download_url, '_blank');
    } catch (err) {
      clearInterval(timer);
      setState('failed');
      setError(err.message || 'Download preparation failed');
    }
  }

  if (state === 'preparing') {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span>Extending and preparing your video for download… {elapsed}s</span>
      </div>
    );
  }

  if (state === 'failed') {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-red-400">{error}</span>
        <button onClick={handleDownload} className="text-indigo-400 hover:text-indigo-300 underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={handleDownload}
      className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors"
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
      {job.download_url ? 'Download Extended Video' : 'Prepare Extended Download'}
    </button>
  );
}

function ReviewSection({ job, extendHours, setExtendHours, availableExtendHours, isExtending, onExtend, onDownloadReady }) {
  const renderTime = job.started_at && job.completed_at
    ? Math.round((new Date(job.completed_at) - new Date(job.started_at)) / 1000)
    : null;

  const masterDuration = job.output_duration_seconds ? formatDuration(job.output_duration_seconds) : null;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">
          Master Preview{masterDuration ? ` (${masterDuration})` : ''}
        </h3>
        {job.master_attachment_url && (
          <a
            href={job.master_attachment_url}
            className="text-indigo-400 hover:text-indigo-300 text-sm font-medium"
          >
            Download Master
          </a>
        )}
      </div>

      {/* Video player */}
      <div className="bg-black rounded-lg overflow-hidden mb-4">
        <video
          controls
          src={job.master_download_url}
          className="w-full max-h-[480px]"
          preload="metadata"
        />
      </div>

      {/* Metadata */}
      <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-400">
        {job.output_size_bytes && (
          <span>Size: {formatFileSize(job.output_size_bytes)}</span>
        )}
        {renderTime !== null && (
          <span>Render time: {formatDuration(renderTime)}</span>
        )}
      </div>

      {/* Extended download */}
      <div className="mb-2">
        <DownloadButton job={job} onDownloadReady={onDownloadReady} />
        <p className="text-gray-500 text-xs mt-2">
          Downloads a seamlessly looped version of your master, extended to the target duration below. First download may take 1–3 minutes to prepare.
        </p>
      </div>

      {/* Extension duration */}
      <div className="mt-6 pt-6 border-t border-gray-800">
        <h4 className="text-sm font-medium text-white mb-2">Target Duration</h4>
        <p className="text-gray-500 text-xs mb-3">Choose how long your extended video will be. This is the version you'll upload to YouTube.</p>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex flex-wrap gap-2">
            {availableExtendHours.map((h) => (
              <button
                key={h}
                onClick={() => setExtendHours(h)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  extendHours === h
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                {h}hr
              </button>
            ))}
          </div>
          <button
            onClick={onExtend}
            disabled={isExtending}
            className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {isExtending ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner size="sm" />
                Extending...
              </span>
            ) : (
              'Extend to ' + extendHours + 'hr'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
